<template>
  <auth-container>
    <auth-card id="login-card" title="ログイン" :title-icon="loginIcon">
      <template #content>
        <form id="login-form" @submit.prevent="submitLogin">
          <auth-instructions>{{ "user.authentication.org-only" | __ }}</auth-instructions>

          <auth-error v-if="error && !processing">{{ error | __ }}</auth-error>

          <jz-container class="form-content">
            <jz-input
              autocomplete="username"
              class="login-card-input"
              data-form-name="authentication"
              name="email"
              placeholder="メールアドレス"
              :value="email"
              type="email"
              @input="val => {}"
              @input-backward-compatible="updateFormStore"
            />

            <jz-input
              autocomplete="current-password"
              @change="handleInputChange"
              v-model="password"
              class="login-card-input"
              data-form-name="authentication"
              name="password"
              placeholder="パスワード"
              type="password"
            />
          </jz-container>

          <jz-button-group>
            <jz-button :disabled="buttonDisabled" primary rounded type="submit">ログイン</jz-button>
          </jz-button-group>

          <jz-container class="forgot-password-notice">
            {{ "user.authentication.forgotPassword.phrase" | __ }}
            <router-link :to="{ name: passwordChangeRequestRoute }">
              {{ "user.authentication.forgotPassword.link" | __ }}
            </router-link>

            <template v-if="!isProductionEnv">
              <br />

              <span class="commit-hash">
                {{ "user.settings.hash" | __(commitHash) }}
              </span>
            </template>
          </jz-container>
        </form>
      </template>
    </auth-card>
  </auth-container>
</template>

<script>
import { mapState } from "vuex";
import loginIcon from "@joyz/app-core/assets/icons/lightblue/LogIn.svg";
import AuthCard from "@tt-app/components/user/auth/AuthCard.vue";
import AuthContainer from "@tt-app/components/user/auth/AuthContainer.vue";
import AuthError from "@tt-app/components/user/auth/AuthError.vue";
import AuthInstructions from "@tt-app/components/user/auth/AuthInstructions.vue";
import {
  ROUTES,
  VUEX_FORM_MUTATION_NAMES,
  VUEX_FORM_NAMESPACE,
  VUEX_USER_ACTION_NAMES,
  VUEX_USER_MUTATION_NAMES,
  VUEX_USER_NAMESPACE
} from "@tt-app/constants";
import GitInfo from "../../../GitInfo.json";

const {
  HOME,
  USER: {
    AUTHENTICATION: { PASSWORD_CHANGE }
  }
} = ROUTES;

const { LOGIN_REQUEST } = VUEX_USER_ACTION_NAMES;

const { UPDATE_FORM } = VUEX_FORM_MUTATION_NAMES;

export default {
  name: "Login",
  components: {
    AuthCard,
    AuthContainer,
    AuthError,
    AuthInstructions
  },
  data() {
    return {
      password: "",
      processing: false
    };
  },
  computed: {
    commitHash() {
      return GitInfo["commitHash"];
    },
    ...mapState({
      token: state => state.user.authentication.token,
      email: state => state.form.authentication.email.value,
      error: state => state.user.authentication.error,
      tmpPasswordValidated: state => state.user.authentication.tmpPasswordValidated
    }),
    buttonDisabled() {
      const emailPresent = this.email !== "";
      const passwordPresent = this.password !== "";
      const inputValuesPresent = emailPresent && passwordPresent && !this.processing;
      return !inputValuesPresent;
    },
    isProductionEnv() {
      return process.env.NODE_ENV === "production";
    }
  },
  watch: {
    token() {
      this.handleAuthenticationStatusChange();
    },
    tmpPasswordValidated() {
      this.handleTmpPasswordChange();
    }
  },
  mounted() {
    this.handleAuthenticationStatusChange();
    this.handleTmpPasswordChange();
  },
  created() {
    // Clean authentication error
    const updateLoginFailure = `${VUEX_USER_NAMESPACE}/${VUEX_USER_MUTATION_NAMES.LOGIN_FAILURE}`;
    this.$store.commit(updateLoginFailure, undefined);

    this.passwordChangeRequestRoute = PASSWORD_CHANGE.REQUEST.NAME;
    this.loginIcon = loginIcon;
  },
  methods: {
    handleAuthenticationStatusChange() {
      if (this.token) {
        const nextPath = this.$route.query.nextPath || HOME.PATH;
        // If login is successful, the next page will replace the login page and
        // login page will not appear in the history
        this.$router.replace({ path: nextPath });
      }
    },
    handleInputChange(event) {
      this.password = event.target.value;
    },
    handleTmpPasswordChange() {
      if (this.tmpPasswordValidated) {
        const nextPage = PASSWORD_CHANGE.FROM_INVITATION.NAME;
        this.$router.push({ name: nextPage });
      }
    },
    updateFormStore(event) {
      const formDetails = {
        formName: event.target.dataset.formName,
        field: event.target.name,
        value: event.target.value
      };
      this.$store.commit(`${VUEX_FORM_NAMESPACE}/${UPDATE_FORM}`, formDetails);
    },
    async submitLogin() {
      this.processing = true;

      const { password } = this;
      const action = `${VUEX_USER_NAMESPACE}/${LOGIN_REQUEST}`;
      await this.$store.dispatch(action, { password });

      this.processing = false;
    }
  }
};
</script>

<style lang="scss">
#login-form {
  .jz-button-group {
    padding: ($gutter * 1.5) 0 $gutter;
  }

  .forgot-password-notice {
    padding-bottom: 24px;
    flex-direction: column;
    color: $color-outer-space;
    font-size: $font-size-xs;
    margin-top: $gutter / 2;
    text-align: center;

    .commit-hash {
      color: var(--color-secondary-dark);
      display: block;
      margin: 16px 0 0 0;
    }
  }
}
</style>
